import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { useAdmin } from "@/context/AdminContext";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import { ArrowRight } from "lucide-react";
import { format, subDays } from "date-fns";
import { fr } from "date-fns/locale";

const KpiCard = ({ label, value, delta, deltaLabel }) => (
  <div className="bg-white rounded-2xl border border-slate-200 p-5">
    <p className="text-xs font-bold uppercase tracking-widest text-brand-blue">{label}</p>
    <p className="font-heading font-extrabold text-3xl text-slate-900 mt-2">{value ?? "—"}</p>
    {delta !== undefined && (
      <p className={`text-xs mt-1 ${delta > 0 ? "text-emerald-600" : "text-slate-400"}`}>
        {delta > 0 ? `↑ ${delta} ` : "-"}{deltaLabel}
      </p>
    )}
  </div>
);

const STATUS_CLS = {
  traite: "bg-emerald-100 text-emerald-700",
  en_cours: "bg-amber-100 text-amber-700",
  nuevo: "bg-slate-100 text-slate-600",
  new: "bg-slate-100 text-slate-600",
};
const STATUS_LBL = { traite: "Traité", en_cours: "En cours", nuevo: "Nouveau", new: "Nouveau" };

const AdminOverviewPage = () => {
  const { stats, quotes, contacts } = useAdmin();

  const chartData = useMemo(() => {
    const days = Array.from({ length: 7 }, (_, i) => {
      const d = subDays(new Date(), 6 - i);
      return {
        key: format(d, "yyyy-MM-dd"),
        day: format(d, "EEE", { locale: fr }),
        devis: 0,
        contacts: 0,
      };
    });
    quotes.forEach((q) => {
      const entry = days.find((d) => d.key === (q.created_at || "").slice(0, 10));
      if (entry) entry.devis += 1;
    });
    contacts.forEach((c) => {
      const entry = days.find((d) => d.key === (c.created_at || "").slice(0, 10));
      if (entry) entry.contacts += 1;
    });
    return days;
  }, [quotes, contacts]);

  const recentItems = useMemo(() => {
    const q = quotes.slice(0, 30).map((item) => ({ ...item, _type: "devis" }));
    const c = contacts.slice(0, 30).map((item) => ({ ...item, _type: "contact" }));
    return [...q, ...c]
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
      .slice(0, 5);
  }, [quotes, contacts]);

  const treatmentRate = quotes.length
    ? Math.round((quotes.filter((q) => q.status === "traite").length / quotes.length) * 100)
    : 0;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <KpiCard label="Devis total" value={stats?.quotes_total} delta={stats?.quotes_24h} deltaLabel="aujourd'hui" />
        <KpiCard label="Contacts" value={stats?.contacts_total} delta={stats?.contacts_24h} deltaLabel="aujourd'hui" />
        <KpiCard label="Sessions chatbot" value={stats?.chats_total} />
        <KpiCard label="Taux traitement" value={`${treatmentRate}%`} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-200 p-5">
          <p className="font-heading font-bold text-slate-900 mb-4">Activité -7 derniers jours</p>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={chartData} barGap={2}>
              <XAxis dataKey="day" tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} width={24} />
              <Tooltip contentStyle={{ borderRadius: "8px", border: "1px solid #e2e8f0", fontSize: "12px" }} />
              <Legend wrapperStyle={{ fontSize: "12px" }} />
              <Bar dataKey="devis" name="Devis" fill="#166534" radius={[4, 4, 0, 0]} />
              <Bar dataKey="contacts" name="Contacts" fill="#0284c7" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-5 flex flex-col">
          <p className="font-heading font-bold text-slate-900 mb-4">Activité récente</p>
          <div className="space-y-2 flex-1">
            {recentItems.map((item) => {
              const isDevis = item._type === "devis";
              const name = isDevis
                ? (item.data?.name || `${item.data?.first_name || ""} ${item.data?.last_name || ""}`.trim())
                : `${item.first_name || ""} ${item.last_name || ""}`.trim();
              const st = item.status || "nuevo";
              return (
                <Link
                  key={item.id}
                  to={`/admin/${isDevis ? "devis" : "contacts"}/${item.id}`}
                  className="flex items-center gap-3 p-2.5 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors group"
                  data-testid={`recent-${item.id}`}
                >
                  <span className={`w-2 h-2 rounded-full flex-shrink-0 ${isDevis ? "bg-brand-green" : "bg-brand-blue"}`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-slate-900 truncate">{name || "—"}</p>
                    <p className="text-[10px] text-slate-400">
                      {new Date(item.created_at).toLocaleString("fr-FR", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                    </p>
                  </div>
                  {isDevis && (
                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full flex-shrink-0 ${STATUS_CLS[st] || STATUS_CLS.nuevo}`}>
                      {STATUS_LBL[st] || "Nuevo"}
                    </span>
                  )}
                  <ArrowRight className="h-3 w-3 text-slate-300 group-hover:text-slate-500 flex-shrink-0" />
                </Link>
              );
            })}
            {recentItems.length === 0 && (
              <p className="text-sm text-slate-400 text-center py-8">Aucune activité récente.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminOverviewPage;
